import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { steps } from '../content/steps'
import type { ActivityState } from '../types'
import {
  addElapsedSeconds,
  createInitialActivityState,
  loadActivityState,
  persistActivityState,
  setCurrentStep,
  setFieldValue,
  setInstructionProgress,
} from './activity-store'
import {
  ActivityContext,
  type ActivityContextValue,
} from './activity-context'

export function ActivityProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActivityState>(() => loadActivityState())

  useEffect(() => {
    persistActivityState(state)
  }, [state])

  useEffect(() => {
    const currentId = steps[state.currentStepIndex]?.id
    if (!currentId) return

    const interval = window.setInterval(() => {
      setState((current) => addElapsedSeconds(current, currentId, 5))
    }, 5000)

    return () => window.clearInterval(interval)
  }, [state.currentStepIndex])

  const updateField = useCallback(
    (stepId: string, key: string, value: string) => {
      setState((current) => setFieldValue(current, stepId, key, value))
    },
    [],
  )

  const updateInstructionProgress = useCallback(
    (stepId: string, index: number) => {
      setState((current) =>
        setInstructionProgress(current, stepId, index),
      )
    },
    [],
  )

  const goToStep = useCallback((index: number) => {
    setState((current) => setCurrentStep(current, index))
  }, [])

  const resetActivity = useCallback(() => {
    setState(createInitialActivityState())
  }, [])

  const value = useMemo<ActivityContextValue>(
    () => ({
      state,
      updateField,
      updateInstructionProgress,
      goToStep,
      resetActivity,
    }),
    [
      state,
      updateField,
      updateInstructionProgress,
      goToStep,
      resetActivity,
    ],
  )

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  )
}
